import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { t } from "@bookingcom/lingojs-core";
import { Carousel, Stack, Text, Title, useViewport } from "@bookingcom/bui-react";
import { useI18n } from "@bookingcom/lingojs-react";
import type CarouselProps from "@bookingcom/bui-react/components/Carousel";

import { getWeekendGetaways } from "store/weekendGetaways/selectors";
import { PriceDisclaimerDesktop } from "components/elements/PriceDisclaimer/PriceDisclaimer.desktop";
import styles from "./WeekendGetaways.module.css";
import WeekendGetawaysItem from "./WeekendGetawaysItem";
import useUserAgent from "hooks/useUserAgent";
import { WEEKEND_GETAWAYS_DAYS, WEEKEND_GETAWAYS_DAYS_V2 } from "../../../constants";
import { useTrackLandingPageViewport } from "hooks/c360/useLandingPageEvents";
import usePointOfSale from "hooks/usePointOfSale";
import { trackWithDefaultStage } from "@flights/et-universal";

type CarouselSize = React.ComponentProps<typeof CarouselProps>["size"];

const WeekendGetaways = () => {
  const [weekendGetawaysDays, setWeekendGetawaysDays] = useState(WEEKEND_GETAWAYS_DAYS);
  const i18n = useI18n();
  const { isSmall, isMedium } = useViewport();
  const { isMobile } = useUserAgent();
  const scrollRef = useRef<HTMLDivElement>(null);
  const pos = usePointOfSale() || "";

  const weekendGetaways = useSelector(getWeekendGetaways);

  const cityName = weekendGetaways?.[0]?.departureCityName ?? "";

  const carouselSize = useMemo((): CarouselSize => {
    let size: CarouselSize = "medium";
    if (isMedium) {
      size = "large";
    } else if (isSmall) {
      size = "largest";
    }

    return size;
  }, [isSmall, isMedium]);

  useTrackLandingPageViewport(scrollRef, "weekend_getaways");

  useEffect(() => {
    if (
      (!isMobile && pos === "us" && trackWithDefaultStage("flights_web_cat_weekend_breaks_us_v2_www", 1) === 2) ||
      (!isMobile && pos !== "us" && trackWithDefaultStage("flights_web_cat_weekend_breaks_row_v2_www", 1) === 2) ||
      (isMobile && pos === "us" && trackWithDefaultStage("flights_web_cat_weekend_breaks_us_v2_mdot", 1) === 2) ||
      (isMobile && pos !== "us" && trackWithDefaultStage("flights_web_cat_weekend_breaks_row_v2_mdot", 1) === 2)
    ) {
      setWeekendGetawaysDays(WEEKEND_GETAWAYS_DAYS_V2);
    }
  }, [isMobile, pos]);

  if (!weekendGetaways?.length) return null;

  return (
    <div ref={scrollRef}>
      <Title
        title={i18n.trans(
          t("flights_cheap_weekend_carousel_title", {
            variables: { num_days: weekendGetawaysDays },
            num_exception: weekendGetawaysDays
          })
        )}
        subtitle={
          <Stack direction="row" justifyContent="start" alignItems="center" wrap="wrap">
            {i18n.trans(t("flights_cheap_weekend_carousel_subtitle", { variables: { city_name: cityName } }))}{" "}
            {!isMobile ? <PriceDisclaimerDesktop weekendGetaways /> : null}
          </Stack>
        }
        variant="headline_2"
        titleTagName="h2"
      />
      <Carousel
        size={isMobile ? "largest" : carouselSize}
        nextButtonAriaLabel={i18n.trans(t("a11y_flights_route_carousel_next_button_aria_label"))}
        previousButtonAriaLabel={i18n.trans(t("a11y_flights_route_carousel_previous_button_aria_label"))}
        className={styles.carousel}
      >
        {weekendGetaways.map((item, idx) => (
          <WeekendGetawaysItem key={idx} item={item} />
        ))}
      </Carousel>
      {isMobile ? (
        <Text variant="small_2" className={styles.disclaimer}>
          {i18n.trans(t("flights_cheap_weekend_carousel_disclaimer"))}
        </Text>
      ) : null}
    </div>
  );
};

export default WeekendGetaways;
